import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import queryString from "query-string";
import useInterval from "../../reactHooks/useInterval";
import useRequest from "../../reactHooks/useRequest";
import { useToggleState } from "@yoast/ui-library";
import isEmpty from "lodash/isEmpty";
import { noop } from "shared-frontend/functions/noop";
import { identity } from "lodash/util";


/**
 * @typedef {import('../../../../nestjs-server/src/domains/PluginInstallation/' +
 *     'UserIntefrace/dtos/GetInstallationOptionsDto').GetInstallationOptionsResponseDto} InstallationOptions
 */


export const Steps = {
	PROCESSING_ORDER: {
		id: Symbol( "PROCESSING_ORDER" ),
		position: 1,
		canBeReturnedTo: false,
		markAsCompleteImmediately: false,
	},
	INSTALL_DOCS_ADDON: {
		id: Symbol( "INSTALL_DOCS_ADDON" ),
		position: 2,
		canBeReturnedTo: true,
		markAsCompleteImmediately: false,
	},
	CONNECT_SITE: {
		id: Symbol( "CONNECT_SITE" ),
		position: 3,
		canBeReturnedTo: false,
		markAsCompleteImmediately: false,
	},
	DOWNLOAD: {
		id: Symbol( "DOWNLOAD" ),
		position: 4,
		canBeReturnedTo: true,
		markAsCompleteImmediately: false,
	},
	FINISH: {
		id: Symbol( "FINISH" ),
		position: 5,
		canBeReturnedTo: true,
		markAsCompleteImmediately: true,
	},
};


const defaultValue = {
	isPolling: false,
	invoiceNumberParam: "",
	currentStepNumber: 1,
	goToNextStep: noop,
	goToPreviousStep: noop,
	subscriptionIdWithInvitableService: null,
	premiumDownloadUrl: null,
	steps: {
		current: Steps.PROCESSING_ORDER,
		currentIndex: 0,
		currentNumber: 1,
		canGoBack: false,
		next: Steps.FINISH,
		available: [ Steps.PROCESSING_ORDER, Steps.FINISH ],
	},
	connectedSiteURL: "",
	setConnectedSiteURL: noop,
	downloadHasStarted: false,
	markDownloadStarted: noop,
};

export const InstallContext = React.createContext( defaultValue );

/**
 * @param {object} children the passed down children.
 *
 * @returns {object} the children with access to the context.
 */
export const InstallContextProvider = ( { children } ) => {
	const [ availableSteps, setAvailableSteps ]     = useState( [ Steps.PROCESSING_ORDER, Steps.FINISH ] );
	const [ currentStep, setCurrentStep ]           = useState( Steps.PROCESSING_ORDER );
	const [ invoiceNumberParam ]                    = useState( queryString.parse( window.location.search ).invoice_number ?? "" );
	const [ connectedSiteURL, setConnectedSiteURL ] = useState( "" );

	/** @type {{data:InstallationOptions|[], reload: function}} */
	const request = useRequest( `v1/installation/options/${ invoiceNumberParam }`, "GET" );

	const [ isPolling, , , , stopPolling ] = useToggleState( true );
	const [ pollCount, setPollCount ]      = useState( 0 );
	const oneMinuteInMilliseconds          = 60000;
	const delay                            = 4000;
	const maxPollingTime                   = oneMinuteInMilliseconds * 2;

	const [ downloadHasStarted, , , markDownloadStarted ] = useToggleState( false );

	const orderIsProcessing = ! isEmpty( invoiceNumberParam ) && ( isEmpty( request.data?.orderStatus ) || request.data?.orderStatus === "pending" );

	const steps = useMemo( () => {
		const currentStepIndex = availableSteps.findIndex( ( step ) => step === currentStep );
		return {
			current: currentStep,
			currentIndex: currentStepIndex,
			currentNumber: currentStepIndex + 1,
			next: availableSteps.at( currentStepIndex + 1 ),
			// eslint-disable-next-line no-undefined
			previous: ( currentStepIndex > 0 ) ? availableSteps.at( currentStepIndex - 1 ) : undefined,
			canGoBack: ( currentStepIndex > 0 ) ? Boolean( availableSteps.at( currentStepIndex - 1 )?.canBeReturnedTo ) : false,
			available: availableSteps,
		};
	}, [ currentStep, availableSteps ] );

	const goToNextStep = useCallback( () => {
		if ( steps.next ) {
			setCurrentStep( steps.next );
		}
	}, [ steps, setCurrentStep ] );

	const goToPreviousStep = useCallback( () => {
		if ( steps.canGoBack ) {
			setCurrentStep( steps.previous );
		}
	}, [ steps, setCurrentStep ] );

	// Set the available steps based on the request data.
	useEffect( () => {
		const stepsToAdd           = [];
		const hasInvitableServices = ! isEmpty( request.data?.invitableServices );
		const hasDownloadables     = ! isEmpty( request.data?.downloadables );

		if ( hasInvitableServices && ! orderIsProcessing ) {
			stepsToAdd.push( Steps.INSTALL_DOCS_ADDON );
		}
		if ( hasDownloadables && ! orderIsProcessing ) {
			stepsToAdd.push( Steps.CONNECT_SITE, Steps.DOWNLOAD );
		}

		const newSteps = [ ...availableSteps, ...stepsToAdd ]
			.filter( identity )
			.sort( ( a, b ) => a.position - b.position );

		if ( stepsToAdd.length > 0 ) {
			setAvailableSteps( newSteps );
		}
	}, [ request.data, stopPolling, orderIsProcessing ] );

	useEffect( () => {
		if ( availableSteps.length > 2 && isPolling ) {
			stopPolling();
			goToNextStep();
		}
	}, [ availableSteps ] );

	useInterval( () => {
		request.reload();
		setPollCount( prevCount => prevCount + 1 );
	}, isPolling ? delay : null );

	useEffect( () => {
		if ( ( pollCount * delay ) >= maxPollingTime ) {
			stopPolling();
		}
	}, [ pollCount, stopPolling ] );

	const premiumDownloadUrl = useMemo( () => {
		for ( const downloadable of request.data?.downloadables || [] ) {
			for ( const productGroup of downloadable.productGroups || [] ) {
				if ( productGroup.slug === "yoast-seo-wordpress-premium" ) {
					return productGroup.downloadUrl;
				}
			}
		}
		return null;
	}, [ request.data ] );


	const contextValue = {
		steps,
		goToNextStep,
		goToPreviousStep,
		isPolling,
		invoiceNumberParam,
		subscriptionIdWithInvitableService: request.data?.invitableServices?.at( 0 )?.subscriptionId ?? null,
		premiumDownloadUrl,
		connectedSiteURL,
		setConnectedSiteURL,
		downloadHasStarted,
		markDownloadStarted,
	};

	return (
		<InstallContext.Provider value={ contextValue }>
			{ children }
		</InstallContext.Provider>
	);
};

InstallContextProvider.propTypes = {
	children: PropTypes.element.isRequired,
};
